import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { PermissionService } from '@abp/ng.core';

// PrimeNG
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';

// Local
import { ProcessModuleFields } from '../../../../api/process-field/process-field';
import { DatePickerComponent, SectionCardComponent } from '../../../shared/shared.component';
import { MatterPERMRoleList } from '../../../../api/enum/matter-perm-role';
import { MatterEVLStatusList } from '../../../../api/enum/matter-evl-status';

@Component({
  selector: 'app-processmodules-perm',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TabsModule,
    CheckboxModule,
    ButtonModule,
    EditorModule,
    SelectModule,
    RadioButtonModule,
    DatePickerComponent,
    SectionCardComponent,
  ],
  templateUrl: './perm.component.html',
  styleUrls: ['../process-fields.component.scss'],
})
export class PrcocessFieldsPermComponent implements OnInit {
  @Input() data!: ProcessModuleFields;
  @Output() save = new EventEmitter<ProcessModuleFields>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  canEdit = true;
  permRoleList = MatterPERMRoleList;
  matterEVLStatusList = MatterEVLStatusList;

  constructor(private readonly fb: FormBuilder, private readonly permissionService: PermissionService) {}

  ngOnInit(): void {
    // get permission
    this.canEdit = this.permissionService.getGrantedPolicy('GCM.Matter.Edit');

    this.initForm();
    this.setupAuditFieldsControl();

    if (!this.canEdit)
      this.form.disable();
  }

  private initForm(): void {
    this.form = this.fb.group({
      PERMRole: [this.data?.processFields['PERMRole']],
      EVLStatus: [this.data?.processFields['EVLStatus']],
      AnticipatedFiling: [this.data?.processFields['AnticipatedFiling']],
      EmployerHadLayoff: [this.data?.processFields['EmployerHadLayoff']],
      Audited: [this.data?.processFields['Audited']],
      AuditReceived: [{ value: this.data?.processFields['AuditReceived'], disabled: !this.data?.processFields['Audited'] }],
      AuditResponseDue: [{ value: this.data?.processFields['AuditResponseDue'], disabled: !this.data?.processFields['Audited'] }],
      AuditResponseSubmitted: [{ value: this.data?.processFields['AuditResponseSubmitted'], disabled: !this.data?.processFields['Audited'] }],
      Id: [this.data?.processFields['Id']],
    });
  }

  private setupAuditFieldsControl(): void {
    const auditedControl = this.form.get('Audited');
    const auditFields = ['AuditReceived', 'AuditResponseDue', 'AuditResponseSubmitted'];

    auditedControl?.valueChanges.subscribe((isAudited) => {
      auditFields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (isAudited) {
          control?.enable();
        } else {
          control?.disable();
        }
      });
    });
  }

  private resetForm(): void {
    // Reset form to original values
    Object.keys(this.data.processFields).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.setValue(this.data.processFields[key]);
      }
    });

    // Re-apply audit fields control
    const isAudited = this.data.processFields['Audited'];
    const auditFields = ['AuditReceived', 'AuditResponseDue', 'AuditResponseSubmitted'];
    auditFields.forEach(fieldName => {
      const control = this.form.get(fieldName);
      if (isAudited) {
        control?.enable();
      } else {
        control?.disable();
      }
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      const updatedData: ProcessModuleFields = {
        ...this.data,
        processFields: this.form.getRawValue()
      };
      this.save.emit(updatedData);
    }
  }

  onCancel(): void {
    // Reset to original values
    this.resetForm();
    // Emit cancel event
    this.cancelled?.emit();
  }
}
